/* eslint-disable eslint-comments/no-restricted-disable */
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import type { NestedEntryPointsMap } from '@jira/work-item__jsm-work-item-tabs/src/types/NestedEntryPointsMap.tsx';
import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import type { JsmPreventionCenterEntrypointProps } from './JsmPreventionCenter.entrypoint.types.tsx';

import { overviewTabEntryPoint } from './overviewTabEntryPoint.entrypoint.tsx';
import { risksTabEntryPoint } from './risksTabEntryPoint.entrypoint.tsx';
import { riskMitigationTabEntryPoint } from './riskMitigationTabEntryPoint.entrypoint.tsx';
import type { PreventionCenterTabsConfig } from './JsmPreventionCenter.tsx';

const TAB_ENTRY_POINTS: PreventionCenterTabsConfig = {
	overview: overviewTabEntryPoint,
	risks: risksTabEntryPoint,
	riskMitigation: riskMitigationTabEntryPoint,
} as const;

type Props = {
	activeTabKey?: string;
	issueKey?: string;
	cloudId?: string;
};

type PreloadedEntryPoints = Partial<PreventionCenterTabsConfig>;

const isValidTabKey = (key: string | undefined): key is keyof PreventionCenterTabsConfig => {
	return key !== undefined && Object.keys(TAB_ENTRY_POINTS).includes(key);
};

type JsmPreventionCenterEntryPointComponent = EntryPointComponent<
	Record<string, never>,
	PreloadedEntryPoints,
	JsmPreventionCenterEntrypointProps,
	Record<string, never>
>;

export const jsmPreventionCenterEntryPoint: EntryPoint<JsmPreventionCenterEntryPointComponent, {}> =
	createEntryPoint({
		root: JSResourceForUserVisible<JsmPreventionCenterEntryPointComponent>(
			/* webpackChunkName: "async-jsm-prevention-center" */ './JsmPreventionCenter.tsx',
			{
				entryFsSsrLiftImportToModule: true,
			},
		),
		getPreloadProps: ({ activeTabKey, issueKey, cloudId }: Props = {}) => {
			const tabKey: keyof PreventionCenterTabsConfig = isValidTabKey(activeTabKey)
				? activeTabKey
				: 'overview';

			let entryPoints: NestedEntryPointsMap<PreventionCenterTabsConfig>;
			switch (tabKey) {
				case 'overview':
					entryPoints = { overview: { entryPoint: overviewTabEntryPoint, entryPointParams: {} } };
					break;
				case 'risks':
					entryPoints = {
						risks: {
							entryPoint: risksTabEntryPoint,
							entryPointParams: { issueKey, cloudId },
						},
					};
					break;
				case 'riskMitigation':
					entryPoints = {
						riskMitigation: { entryPoint: riskMitigationTabEntryPoint, entryPointParams: {} },
					};
					break;
				default: {
					const _exhaustiveCheck: never = tabKey;
					throw new Error(`Invalid tab key: ${_exhaustiveCheck}`);
				}
			}

			return {
				entryPoints,
			};
		},
	});
